import { FieldProps } from 'formik';
import { ReactElement, useMemo } from 'react';
import DeclarationField from 'views/declarations/common/DeclarationField';
import { getFormikProps } from 'views/declarations/utils/form-utils';
import { useTranslation } from 'react-i18next';
import FormInput from '../../../../../components/ui/composed/declarations/formInput/DeclarationInput';
import useFormUtils from '../../../../../hooks/useFormUtils';
import { AvailableForms } from '../../../../../hooks/useHideEntities';
import { ValidationSchema, validators } from '../validations/validations';

type Fields = 'totalGrossMassMeasure' | 'grossMassMeasure';

const exportGrossMassValidators = [validators.number(), validators.float(16, 6)];
const importGrossMassValidators = [validators.number(), validators.float(16, 6)];

const productGrossMassSchema = (
    fieldValidators: typeof exportGrossMassValidators
): {
    childValidators: {
        'commodity.goodsMeasure': ValidationSchema<Fields>;
    };
} => ({
    childValidators: {
        'commodity.goodsMeasure': {
            childValidators: {
                grossMassMeasure: fieldValidators,
            },
        },
    },
});

export const grossMassBlockValidation: ValidationSchema<Fields> = {
    childValidators: {
        totalGrossMassMeasure: exportGrossMassValidators,
    },
};

export const grossMassBlockProductValidation = productGrossMassSchema(exportGrossMassValidators);

export const cdsImportGrossMassBlockValidation: ValidationSchema<Fields> = {
    childValidators: {
        totalGrossMassMeasure: importGrossMassValidators,
    },
};

export const cdsImportGrossMassBlockProductValidation = productGrossMassSchema(importGrossMassValidators);

interface Props extends BlockProps<Fields>, DeclarationBlockProps<AvailableForms, Fields> {
    isProduct?: boolean;
}

const GrossMassBlock = ({ path, declarationOptions, isProduct }: Props): ReactElement => {
    const { t } = useTranslation('form');
    const { formTypeForCds: formType } = useFormUtils();

    const ucc = useMemo(() => '6/5', []);
    const fieldName = isProduct ? 'grossMassMeasure' : 'totalGrossMassMeasure';

    return (
        <DeclarationField pathPrefix={path} name={fieldName}>
            {({ form, field }: FieldProps<any>) => (
                <FormInput
                    refNumber={ucc}
                    required={declarationOptions?.[formType]?.fieldOptions?.[fieldName]?.required}
                    label={t('goodsItems.grossMass')}
                    {...getFormikProps(field.name, form)}
                    condensed
                />
            )}
        </DeclarationField>
    );
};

export default GrossMassBlock;
